/**
 * RouteRow Component
 *
 * A list row for displaying a route in the list view.
 * Shows route circle with grade, name/wall section, set date, ascent count,
 * and completion status.
 *
 * Features:
 * - Route circle with hold color fill, optional tag color ring, and grade text
 * - Two-line text: route name (primary) + wall section (secondary), or wall section only
 * - Formatted set date (e.g., "Jan 15")
 * - Ascent count
 * - Completion checkmark for sent routes
 *
 * @example
 * ```tsx
 * <RouteRow
 *   route={route}
 *   colorGrades={gym.colorGrades}
 *   primaryMethod={gym.labelingMethods?.primaryMethod ?? 'holdColors'}
 *   isSent={sentRouteIds.has(route.id)}
 *   onClick={() => setSelectedRoute(route)}
 * />
 * ```
 */

import { type CSSProperties, memo } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import type {
  Route,
  ColorGrade,
  VGrade,
  GradeModifier,
  PrimaryIdentificationMethod,
} from '@flash/shared';
import { RouteCircle } from '@/components/icons/RouteCircle';

/**
 * Format a grade with optional modifier for display
 */
const formatGrade = (grade: VGrade, modifier?: GradeModifier | null): string => {
  if (!modifier) return grade;
  return `${grade}${modifier}`;
};

/**
 * Props for RouteRow component
 */
export interface RouteRowProps {
  /** Route to display */
  route: Route;
  /** Gym's color grades for hold colors (or all colors when linked) */
  colorGrades: ColorGrade[];
  /** Gym's tag color grades (only used when colors are unlinked) */
  tagColorGrades?: ColorGrade[] | undefined;
  /** Primary identification method from gym settings */
  primaryMethod?: PrimaryIdentificationMethod;
  /** Whether the user has sent this route */
  isSent: boolean;
  /** Whether this row is currently selected/highlighted */
  isSelected?: boolean;
  /** Called when the row is clicked */
  onClick: () => void;
}

/**
 * Format the set date of a route for display (e.g., "Jan 15")
 */
const formatSetDate = (setDate: Date | { seconds: number }): string => {
  const d =
    setDate instanceof Date ? setDate : new Date((setDate as { seconds: number }).seconds * 1000);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

/**
 * Get the hex color value for a color name from the gym's color grades
 */
const getColorHex = (colorName: string | undefined, colorGrades: ColorGrade[]): string | null => {
  if (!colorName) return null;
  const colorGrade = colorGrades.find((cg) => cg.displayName === colorName);
  return colorGrade?.color ?? null;
};

/**
 * RouteRow Component
 */
export const RouteRow = memo(function RouteRow({
  route,
  colorGrades,
  tagColorGrades,
  isSent,
  isSelected = false,
  onClick,
}: RouteRowProps) {
  // Determine which color grades to use for tag lookups
  // If tagColorGrades is provided (unlinked mode), use it; otherwise use colorGrades
  const effectiveTagColorGrades = tagColorGrades ?? colorGrades;

  // Get hold and tag colors from route identification
  const holdColorHex = getColorHex(route.identification?.holdColor, colorGrades);
  const tagColorHex = getColorHex(route.identification?.tagColor, effectiveTagColorGrades);

  // Fallback to legacy colorGrade if no identification colors
  const hasIdentificationColors = holdColorHex || tagColorHex;
  const fallbackColor = !hasIdentificationColors
    ? (colorGrades.find((cg) => cg.displayName === route.colorGrade)?.color ?? '#808080')
    : null;

  return (
    <button
      style={{
        ...rowStyle,
        ...(isSent ? sentRowStyle : {}),
        ...(isSelected ? selectedRowStyle : {}),
      }}
      onClick={onClick}
      aria-label={`${route.officialGrade ? formatGrade(route.officialGrade, route.officialGradeModifier) : route.wallSection}${route.name ? ` ${route.name}` : ''}, ${isSent ? 'sent' : 'not sent'}`}
    >
      {/* Route Circle */}
      <div style={circleContainerStyle} aria-hidden="true">
        <RouteCircle
          grade={
            route.officialGrade ? formatGrade(route.officialGrade, route.officialGradeModifier) : ''
          }
          holdColor={holdColorHex ?? fallbackColor ?? '#808080'}
          tagColor={tagColorHex ?? undefined}
          size={24}
        />
      </div>

      {/* Text Content */}
      <div style={textContentStyle}>
        {route.name ? (
          <>
            <span style={primaryTextStyle}>{route.name}</span>
            <span style={secondaryTextStyle}>{route.wallSection}</span>
          </>
        ) : (
          <span style={primaryTextStyle}>{route.wallSection}</span>
        )}
      </div>

      {/* Position Badge */}
      {route.position && (
        <span style={positionBadgeStyle}>
          {route.position === 'left' ? 'L' : route.position === 'right' ? 'R' : 'C'}
        </span>
      )}

      {/* Draft Badge */}
      {route.status === 'draft' && <span style={draftBadgeStyle}>Draft</span>}

      {/* Set Date */}
      <span style={dateStyle}>{formatSetDate(route.setDate)}</span>

      {/* Send Count */}
      <span style={ascentStyle}>{route.totalSends} sends</span>

      {/* Completion Status */}
      <div style={statusStyle}>
        {isSent ? <CheckCircleIcon style={checkIconStyle} /> : <div style={emptyCircleStyle} />}
      </div>
    </button>
  );
});

// Styles
const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  width: 'calc(100% - 16px)',
  margin: '8px',
  padding: '14px 16px',
  backgroundColor: 'var(--color-bg-primary, white)',
  border: 'none',
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)',
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'background-color 150ms ease-out, box-shadow 150ms ease-out',
};

const sentRowStyle: CSSProperties = {
  opacity: 0.6,
};

const selectedRowStyle: CSSProperties = {
  backgroundColor: 'var(--color-primary-light, #dbeafe)',
  boxShadow: '0 0 0 2px var(--color-primary, #3b82f6), 0 2px 4px rgba(0, 0, 0, 0.1)',
  animation: 'highlightPulse 0.4s ease-out',
};

const circleContainerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
};

const textContentStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '2px',
  minWidth: 0,
  flex: 1,
};

const primaryTextStyle: CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'var(--color-text-primary, #1f2937)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const secondaryTextStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-secondary, #6b7280)',
};

const positionBadgeStyle: CSSProperties = {
  fontSize: '0.625rem',
  fontWeight: 600,
  color: 'var(--color-text-secondary, #6b7280)',
  backgroundColor: 'var(--color-bg-tertiary, #f3f4f6)',
  padding: '2px 6px',
  borderRadius: '4px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  flexShrink: 0,
};

const draftBadgeStyle: CSSProperties = {
  fontSize: '0.625rem',
  fontWeight: 600,
  color: '#f59e0b',
  backgroundColor: 'rgba(245, 158, 11, 0.15)',
  padding: '2px 6px',
  borderRadius: '4px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  flexShrink: 0,
};

const dateStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-secondary, #6b7280)',
  whiteSpace: 'nowrap',
};

const ascentStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-secondary, #6b7280)',
  whiteSpace: 'nowrap',
  textAlign: 'right',
};

const statusStyle: CSSProperties = {
  width: '24px',
  height: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const checkIconStyle: CSSProperties = {
  fontSize: '20px',
  color: '#22c55e',
};

const emptyCircleStyle: CSSProperties = {
  width: '18px',
  height: '18px',
  borderRadius: '50%',
  border: '2px solid var(--color-border, #e5e7eb)',
};

export default RouteRow;
